import { memo } from 'react';
import { STANDARD_SCALES, formatScale, paperLabel, textSizeToWorldCapHeight, type TextSize } from 'shared-types';
import { Option, Select } from '../../components/inputs/Select';
import { T, useT } from '../../translation/src';
import type { PrintTemplateChoice, QuickFraming } from './export/useQuickPdfExport';

/** ISO 3098 lettering heights (capital height, mm). */
const CAP_HEIGHTS_MM = [1.8, 2.5, 3.5, 5, 7] as const;

export interface PdfExportSettings {
  templateKey: string;
  scale: number | 'fit';
  /** Capital height (mm on paper) of labels and dimensions. */
  labelCapMm: number;
  /** Capital height (mm on paper) of room tags. */
  roomTagCapMm: number;
  /** Framed by hand in the preview (null: every floor centered at the chosen scale). */
  framing: QuickFraming | null;
  /**
   * Export through one of the project's sheets (its template, framing,
   * layers, pens, text sizes, fields and revisions) instead of the template
   * defaults; null = template defaults, undefined = not chosen yet (the most
   * recently edited sheet when there is one).
   */
  sheetId?: string | null;
}

/** A sheet the export can follow. */
export interface PdfExportSheetChoice {
  id: string;
  name: string;
  number?: string | null;
}

/** The "layout" value for the template defaults. */
const TEMPLATE_LAYOUT = '__template';

export const templateKeyOf = (choice: Pick<PrintTemplateChoice, 'source' | 'id'>): string => `${choice.source}:${choice.id}`;

export const capSize = (mm: number): TextSize => ({ value: mm, unit: 'mm', basis: 'cap' });

interface PdfExportOptionsProps {
  choices: PrintTemplateChoice[];
  value: PdfExportSettings;
  onChange: (value: PdfExportSettings) => void;
  sheets: readonly PdfExportSheetChoice[];
}

const formatMeters = (meters: number) => `${Math.round(meters * 100) / 100} m`;

const CapHeightSelect: React.FC<{ value: number; scale: number | 'fit'; onChange: (mm: number) => void }> = ({ value, scale, onChange }) => (
  <div className="flex items-center gap-2">
    <div className="w-28">
      <Select anchored value={value} onChange={(e) => onChange(e.target.value)} renderSelectedValue={(mm) => `${mm} mm`}>
        {CAP_HEIGHTS_MM.map((mm) => (
          <Option key={mm} value={mm}>
            {mm} mm
          </Option>
        ))}
      </Select>
    </div>
    {/* What the lettering measures in the building, so its size on the plan is never a guess. */}
    {scale !== 'fit' && <span className="text-xs text-gray-500">= {formatMeters(textSizeToWorldCapHeight(capSize(value), scale))}</span>}
  </div>
);

const sheetLabel = (sheet: PdfExportSheetChoice) => `${sheet.number ? `${sheet.number} · ` : ''}${sheet.name}`;

const PdfExportOptions_: React.FC<PdfExportOptionsProps> = ({ choices, value, onChange, sheets }) => {
  const selected = choices.find((c) => templateKeyOf(c) === value.templateKey);
  const page = selected?.spec.pages[0];
  const sheet = value.sheetId ? sheets.find((s) => s.id === value.sheetId) : undefined;
  const templateLabel = useT('template defaults', { swc: true });
  const layout = (
    <>
      <span>
        <T _str="layout" swc />
      </span>
      <Select
        anchored
        value={sheet ? sheet.id : TEMPLATE_LAYOUT}
        onChange={(e) => onChange({ ...value, sheetId: e.target.value === TEMPLATE_LAYOUT ? null : e.target.value })}
        renderSelectedValue={(id) => (id === TEMPLATE_LAYOUT ? templateLabel : sheetLabel(sheets.find((s) => s.id === id) ?? { id, name: id }))}
      >
        {sheets.map((s) => (
          <Option key={s.id} value={s.id}>
            {sheetLabel(s)}
          </Option>
        ))}
        <Option value={TEMPLATE_LAYOUT}>{templateLabel}</Option>
      </Select>
    </>
  );
  if (sheet) {
    // A sheet decides everything about the page: nothing to set here.
    return (
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3 pt-4 text-sm">
        {layout}
        <span />
        <span className="text-xs text-gray-500">
          <T _str="exports exactly like the sheet: its template, framing, layers, pens, text sizes, fields and revisions, for the floors ticked above. Change it in Sheets." swc />
        </span>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3 pt-4 text-sm">
      {sheets.length > 0 && layout}
      <span>
        <T _str="template" swc />
      </span>
      <Select anchored value={value.templateKey} onChange={(e) => onChange({ ...value, templateKey: e.target.value })} renderSelectedValue={(key) => choices.find((c) => templateKeyOf(c) === key)?.name ?? key}>
        {choices.map((choice) => (
          <Option key={templateKeyOf(choice)} value={templateKeyOf(choice)}>
            {choice.name}
          </Option>
        ))}
      </Select>
      {page && (
        <>
          <span />
          <span className="text-xs text-gray-500">
            {paperLabel(page.paper)} · <T _str={page.orientation} /> · {selected.description}
          </span>
        </>
      )}
      <span>
        <T _str="scale" swc />
      </span>
      <Select<number | 'fit'> anchored value={value.scale} onChange={(e) => onChange({ ...value, scale: e.target.value })} renderSelectedValue={(scale) => (scale === 'fit' ? <T _str="fit to floor" swc /> : formatScale(scale))}>
        <Option value="fit">
          <T _str="fit to floor" swc />
        </Option>
        {STANDARD_SCALES.filter((s) => s >= 10).map((scale) => (
          <Option key={scale} value={scale}>
            {formatScale(scale)}
          </Option>
        ))}
      </Select>
      <span>
        <T _str="text height" swc />
      </span>
      <CapHeightSelect value={value.labelCapMm} scale={value.scale} onChange={(labelCapMm) => onChange({ ...value, labelCapMm })} />
      <span>
        <T _str="room tag height" swc />
      </span>
      <CapHeightSelect value={value.roomTagCapMm} scale={value.scale} onChange={(roomTagCapMm) => onChange({ ...value, roomTagCapMm })} />
    </div>
  );
};

export const PdfExportOptions = memo(PdfExportOptions_);
